import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import axios from "axios";
import { logError } from "@/lib/logger";
import { capture, identify, resetIdentity } from "@/lib/analytics";
import {
  EVENTS,
  captureOnce,
  clearCaptureOnce,
  deviceType,
} from "@/lib/analyticsEvents";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
    // API & axios are module-level stable references — intentionally no deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Lu au premier rendu, avant que l'effet ci-dessous ne nettoie l'URL : la
  // distinction inscription / connexion doit rester disponible plus tard, quand
  // la session asynchrone se résout.
  const isNewAccount = useRef(
    new URLSearchParams(window.location.search).get("new_account") === "1"
  );

  // Fire account_created once when the backend flags a first-time signup on the
  // post-OAuth redirect (?new_account=1), then strip the marker from the URL.
  useEffect(() => {
    if (!isNewAccount.current) return;
    const params = new URLSearchParams(window.location.search);
    capture(EVENTS.ACCOUNT_CREATED, {
      method: "google",
      device_type: deviceType(),
    });
    params.delete("new_account");
    const qs = params.toString();
    window.history.replaceState(
      {},
      "",
      window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash
    );
  }, []);

  // Le visiteur anonyme devient identifié dès que la session est résolue, quelle
  // que soit la porte d'entrée (OAuth Google, lien magique, cookie déjà posé) :
  // c'est le seul point du code où l'on apprend qui est la personne.
  //
  // `login` se déduit de ce même moment, mais la résolution de session rejoue à
  // chaque rechargement alors que la connexion n'a eu lieu qu'une fois, d'où
  // captureOnce. Une inscription n'émet pas `login` : account_created dit déjà
  // ce qui s'est passé, et compter les deux fausserait le volume de connexions.
  useEffect(() => {
    if (!user) return;
    identify(user);
    // Consommé, et non seulement lu : si la personne se déconnecte puis se
    // reconnecte dans le même onglet, cette seconde arrivée est bien un `login`.
    if (isNewAccount.current) {
      isNewAccount.current = false;
      return;
    }
    captureOnce(EVENTS.LOGIN, {
      actor_type: user.actor_type,
      device_type: deviceType(),
    });
  }, [user]);

  const login = (redirectPath) => {
    // Native Google OAuth: hand off to the backend, which redirects to Google
    // and, on callback, sets the session cookie before sending us back here.
    const target = redirectPath || window.location.pathname;
    const params = new URLSearchParams({ redirect: target });
    window.location.href = `${API}/auth/google/login?${params.toString()}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      logError("Logout request failed:", err);
    }
    setUser(null);
    // L'ordre compte : l'événement doit encore porter l'identité du compte qui
    // se déconnecte, la remise à zéro vient juste après. Sans elle, la personne
    // suivante sur le même appareil serait comptée comme celle qui part.
    capture(EVENTS.LOGOUT);
    resetIdentity();
    clearCaptureOnce();
  };

  // Passwordless: ask the backend to email a one-time sign-in link. The backend
  // always answers generically (no account enumeration); we surface success on
  // any 2xx and only bubble up genuine network errors to the caller.
  const requestMagicLink = async (email, redirectPath) => {
    const redirect = redirectPath || window.location.pathname;
    await axios.post(
      `${API}/auth/magic/request`,
      { email, redirect },
      { withCredentials: true }
    );
  };

  const value = useMemo(
    () => ({ user, loading, login, logout, requestMagicLink, setUser, refresh: checkAuth }),
    // login/logout/requestMagicLink are stable closures; user & loading drive re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, loading, checkAuth]
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}

export default AuthProvider;
